import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to manage fullscreen mode
 * Handles both standard Fullscreen API and iOS Safari workarounds
 */
export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [hasAutoEntered, setHasAutoEntered] = useState(false);

  useEffect(() => {
    // Check if Fullscreen API is supported
    const supported = !!(
      document.fullscreenEnabled ||
      (document as any).webkitFullscreenEnabled ||
      (document as any).mozFullScreenEnabled ||
      (document as any).msFullscreenEnabled
    );
    
    setIsSupported(supported);

    // Detect iOS (no Fullscreen API support)
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    console.log('📱 Fullscreen detection:', { supported, iOS });

    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      const fullscreenElement = 
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;
      
      setIsFullscreen(!!fullscreenElement);
      console.log('📱 Fullscreen state changed:', !!fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const enterFullscreen = useCallback(async () => {
    if (isIOS) {
      // iOS Safari: Scroll to hide address bar
      console.log('📱 iOS detected: Using scroll workaround');
      window.scrollTo(0, 1);
      setIsFullscreen(true);
      return;
    }

    if (!isSupported) {
      console.warn('📱 Fullscreen API not supported');
      return;
    }

    const element = document.documentElement;

    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      }
      console.log('📱 Entered fullscreen mode');
    } catch (error) {
      console.error('📱 Error entering fullscreen:', error);
    }
  }, [isSupported, isIOS]);

  const exitFullscreen = useCallback(async () => {
    if (isIOS) {
      // iOS doesn't have real fullscreen to exit
      setIsFullscreen(false);
      return;
    }

    if (!document.fullscreenElement) {
      return;
    }

    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
      console.log('📱 Exited fullscreen mode');
    } catch (error) {
      console.error('📱 Error exiting fullscreen:', error);
    }
  }, [isIOS]);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  /**
   * Automatically enter fullscreen on first gesture
   * Returns true if fullscreen was triggered, false if already triggered before
   */
  const autoEnterOnGesture = useCallback(async () => {
    if (hasAutoEntered) {
      return false;
    }
    
    console.log('📱 Auto-entering fullscreen on first gesture');
    setHasAutoEntered(true);
    await enterFullscreen();
    return true;
  }, [hasAutoEntered, enterFullscreen]);

  return {
    isFullscreen,
    isSupported: isSupported || isIOS,
    isIOS,
    hasAutoEntered,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
    autoEnterOnGesture,
  };
}
